import { Client } from '@kcftech/prometheustypescriptapiclient';
import { Auth } from 'auth-provider';
import { IUserContext } from 'contexts/UserContext';
import { logout } from 'pages/Login/Login';

const getHeaders = (
    accessToken: string,
    corporationId: string | undefined,
    locationId: string | undefined,
    options?: RequestInit
): Headers => {
    const headers = new Headers(options?.headers);
    // Checking for form data here to indicate a file is being uploaded.
    // Rest of our app does not use FormData so we assume here.
    // Do not set content type when uploading file. Browser will take care of this.
    if (!(options?.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
    }
    headers.set('Authorization', `Bearer ${accessToken}`);
    headers.set('x-KCF-corp_id', corporationId ?? '');
    headers.set('x-KCF-location_id', locationId ?? '');
    return headers;
};
export class ClientHelper {
    public sdClient: Client;

    public sdClientMetadata: { PrometheusApiVersion: string | undefined } = {
        PrometheusApiVersion: undefined,
    };

    constructor() {
        const api = process.env.REACT_APP_API_BASE_URL;
        this.sdClient = new Client(api, {
            fetch: async (url: RequestInfo, init?: RequestInit) => {
                const controller = new AbortController();
                const fetchPromise = fetch(url, {
                    ...init,
                    signal:
                        // global mocks introduce an incompatibility with nodejs internals, it seems
                        process.env.NODE_ENV !== 'test' ? controller.signal : undefined,
                });

                // Add cancel method to promise
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (fetchPromise as any).cancel = () => controller.abort();

                fetchPromise.then((res) => {
                    this.sdClientMetadata.PrometheusApiVersion =
                        res.headers.get('prom-svc-version') ?? undefined;

                    if (res.status === 401) {
                        logout();
                    }
                });
                return fetchPromise;
            },
        });
        this.addHeaders(undefined, undefined, undefined);
    }

    addHeaders = (corporationId?: string, locationId?: string, user?: IUserContext) => {
        this.sdClient.transformOptions = getTransformOptions(
            corporationId,
            locationId,
            user
        );
    };
}

const getTransformOptions = (
    corporationId?: string,
    locationId?: string,
    user?: IUserContext
) => async (options: RequestInit): Promise<RequestInit> => {
    // NOTE: The reason for calling currentSession() is because we want
    // to check that the token is still valid, for example, if it has
    // expired or no longer exists. If the token is expired but can be
    // refreshed, then that will happen implicitly in the call to currentSession().
    // We only reject if an exception is thrown (i.e., could not
    // reauthenticate to the server).
    try {
        const session = await Auth.currentSession();
        return {
            ...options,
            referrerPolicy: 'strict-origin-when-cross-origin',
            headers: getHeaders(
                session.getAccessToken().getJwtToken(),
                corporationId,
                locationId,
                options
            ),
        };
    } catch (err) {
        // We may not have an authenticated session (e.g., logging in and
        // need to call auth APIs), and calling Auth.currentSession() will
        // not find the session, so Amplify will throw an exception. If the
        // user was not considered authenticated then, it's okay to just
        // resolve the promise. If the user was already authenticated and we
        // got an error, then something is wrong so we reject the promise.
        if (!user?.authenticated) {
            return options;
        }
        throw err;
    }
};
